import { useAppSelector } from 'flow/hooks/useRedux';
import useAppContext from 'flow/hooks/useAppContext';
import { CallRecordKey } from 'flow/types/EntityKeys';
import { getContactName } from 'flow/utils/contact-utils';
import { isCallRecordAMeeting } from 'flow/utils/call-records-utils';
import { selectCallRecordChatSessionDisplayName } from 'flow/selectors/call-selectors';
import { useCallRecordWithCtiContactByKey } from 'flow/hooks/useCallRecordWithCtiContact';
import CallRecordAvatar from 'flow/components/call-history-v2/CallRecordAvatar';
import ContactTitle from 'flow/components/pbx/v2/pbx-queue-items/history/call-record/ContactTitle';
import ContactSubtitle from 'flow/components/pbx/v2/pbx-queue-items/history/call-record/ContactSubtitle';
import ContactSubtitleExtended from 'flow/components/pbx/v2/pbx-queue-items/history/call-record/ContactSubtitleExtended';

interface Props {
    callRecordKey: CallRecordKey;
    subject?: string;
}

const CallRecordCounterpartIdentity = ({ callRecordKey, subject }: Props) => {
    const { i18next } = useAppContext();
    const callRecord = useCallRecordWithCtiContactByKey(callRecordKey);

    const videoDisplayName = useAppSelector((state) =>
        selectCallRecordChatSessionDisplayName(state, callRecordKey),
    );

    if (!callRecord) return null;

    const contact = callRecord.get('contact');
    const contactFullName = getContactName(contact);

    const isVideoCall = isCallRecordAMeeting(callRecord);
    const showVideoMeetingPhrase = !subject && isVideoCall;

    const renderSubtitle = () => {
        if (subject) {
            return (
                <ContactSubtitleExtended
                    contact={contact}
                    contactFullName={contactFullName}
                    number={callRecord.get('number')}
                />
            );
        }
        if (showVideoMeetingPhrase) {
            return i18next.t('Call.VideoMeeting');
        }
        return (
            <ContactSubtitle
                contact={contact}
                contactFullName={contactFullName}
                number={callRecord.get('number')}
            />
        );
    };

    return (
        <>
            <CallRecordAvatar
                callRecordKey={callRecordKey}
                size="toolbar"
                hideContactAvatarWhenNoResource={true}
            />
            <div className="flex min-w-0 flex-col">
                <span className="truncate font-bold">
                    {subject ||
                        (showVideoMeetingPhrase ? (
                            videoDisplayName ||
                            i18next.t('videoconference.meeting')
                        ) : (
                            <ContactTitle
                                contact={contact}
                                contactFullName={contactFullName}
                                number={callRecord.get('number')}
                            />
                        ))}
                </span>
                <span className="truncate text-sm text-gray-500">
                    {renderSubtitle()}
                </span>
            </div>
        </>
    );
};

export default CallRecordCounterpartIdentity;
